import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Phone, MessageCircle, Instagram, Facebook, Youtube, Send } from "lucide-react";
import { useApp } from "../contexts/AppContext";
import SiteButton from "./SiteButton";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Footer() {
  const { lang, settings } = useApp();
  const tr = (pt, en) => (lang !== "en" ? pt : en);
  const bg = settings.footer_bg_color || "#0B1D3A";
  const copyright = lang === "en" ? settings.footer_text_en : settings.footer_text_pt;
  const nlTitle = lang === "en" ? settings.newsletter_title_en : settings.newsletter_title_pt;
  const nlSubtitle = lang === "en" ? settings.newsletter_subtitle_en : settings.newsletter_subtitle_pt;

  const email = settings.footer_email;
  const phone = settings.footer_phone;
  const whatsapp = settings.footer_whatsapp;
  const reclamacoes = settings.footer_reclamacoes_url;
  const termsOn = settings.footer_terms_enabled !== false;
  const privacyOn = settings.footer_privacy_enabled !== false;
  const reclamacoesOn = settings.footer_reclamacoes_enabled !== false && !!reclamacoes;
  const termsUrl = settings.footer_terms_url || "/terms";
  const privacyUrl = settings.footer_privacy_url || "/privacy";
  const subscribeLabel = (lang === "en"
    ? (settings.footer_subscribe_label_en || "SUBSCRIBE")
    : (settings.footer_subscribe_label_pt || "SUBSCREVER"));
  const subscribeColor = settings.footer_subscribe_color || "gold";
  const socials = [
    { url: settings.social_instagram, Icon: Instagram, label: "Instagram" },
    { url: settings.social_facebook, Icon: Facebook, label: "Facebook" },
    { url: settings.social_youtube, Icon: Youtube, label: "YouTube" },
  ].filter((s) => s.url);

  const [nlEmail, setNlEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!consent) { toast.error(tr("Aceite receber comunicações para subscrever.", "Please accept to subscribe.")); return; }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/newsletter/subscribe`, { email: nlEmail, consent: true, source: "footer" });
      toast.success(data.already
        ? tr("Já estavas subscrito. Obrigado!", "You're already subscribed. Thanks!")
        : tr("Subscrição feita com sucesso! 🎄", "Successfully subscribed! 🎄"));
      setNlEmail(""); setConsent(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || tr("Não foi possível subscrever.", "Could not subscribe."));
    } finally { setBusy(false); }
  };

  const contactCard = "flex items-center gap-3 rounded-2xl border border-white/12 bg-white/5 px-4 py-4 text-white/90 hover:bg-white/10 transition-colors";

  return (
    <footer className="relative z-30 text-white" style={{ background: bg }} data-testid="site-footer">
      <div className="max-w-6xl mx-auto px-6 md:px-10 pt-12 pb-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-10 lg:gap-12">
          {/* Col 1: Newsletter */}
          <div data-testid="footer-newsletter-col">
            <p className="font-fredoka text-xl font-extrabold tracking-wide text-[#C8A24B]" style={{ textShadow: "0 0 16px rgba(200,162,75,0.45)" }}>{(nlTitle || "Newsletter").toUpperCase()}</p>
            <p className="font-nunito text-white/70 mt-1">{nlSubtitle || tr("Um passo à frente!", "One step ahead!")}</p>
            <form onSubmit={subscribe} className="mt-5 space-y-4" data-testid="newsletter-form">
              <div className="flex items-center gap-3 rounded-2xl border border-white/12 bg-white/5 px-4 py-4 focus-within:border-[#C8A24B] transition-colors">
                <Mail size={18} className="text-white/50 shrink-0" />
                <input
                  type="email" required value={nlEmail} onChange={(e) => setNlEmail(e.target.value)}
                  placeholder={tr("O seu e-mail", "Your e-mail")}
                  className="w-full bg-transparent outline-none text-white placeholder-white/40 font-nunito"
                  data-testid="newsletter-email"
                />
              </div>
              <label className="flex items-start gap-2 text-sm text-white/65 font-nunito cursor-pointer">
                <input type="checkbox" required checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-0.5 h-4 w-4 accent-[#C8A24B]" data-testid="newsletter-consent" />
                <span>{tr("Aceito receber comunicações por e-mail. Posso cancelar a qualquer momento.", "I agree to receive emails. I can unsubscribe anytime.")}</span>
              </label>
              <SiteButton type="submit" disabled={busy} color={subscribeColor} icon={Send}
                fullWidth size="lg" testId="newsletter-submit" format="flat">
                {subscribeLabel}
              </SiteButton>
            </form>
          </div>

          {/* Col 2: Contacto */}
          <div data-testid="footer-contact-col">
            <p className="font-fredoka text-xl font-extrabold tracking-wide text-[#C8A24B] flex items-center gap-2" style={{ textShadow: "0 0 16px rgba(200,162,75,0.4)" }}>
              <span className="h-2 w-2 rounded-full bg-[#C8A24B]" /> {tr("CONTACTO", "CONTACT")}
            </p>
            <div className="mt-5 space-y-4">
              {email && (
                <a href={`mailto:${email}`} className={contactCard} data-testid="footer-email-link">
                  <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-white/10"><Mail size={17} /></span>
                  <span className="font-nunito">{email}</span>
                </a>
              )}
              {phone && (
                <a href={`tel:${phone.replace(/\s/g, "")}`} className={contactCard} data-testid="footer-phone-link">
                  <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-white/10"><Phone size={17} /></span>
                  <span className="font-nunito">{phone}</span>
                </a>
              )}
              {whatsapp && (
                <a href={`https://wa.me/${whatsapp.replace(/[^0-9]/g, "")}`} target="_blank" rel="noreferrer" className={`${contactCard} hover:border-[#25D366]/50`} data-testid="footer-whatsapp-link">
                  <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-[#25D366]/15 text-[#25D366]"><MessageCircle size={17} /></span>
                  <span className="font-nunito">WhatsApp</span>
                </a>
              )}
            </div>
          </div>

          {/* Col 3: Redes Sociais */}
          {socials.length > 0 && (
            <div data-testid="footer-social-col">
              <p className="font-fredoka text-xl font-extrabold tracking-wide text-[#C8A24B] flex items-center gap-2" style={{ textShadow: "0 0 16px rgba(200,162,75,0.4)" }}>
                <span className="h-2 w-2 rounded-full bg-[#C8A24B]" /> {tr("REDES SOCIAIS", "SOCIAL MEDIA")}
              </p>
              <div className="mt-6 flex items-center gap-4">
                {socials.map(({ url, Icon, label }) => (
                  <a key={label} href={url} target="_blank" rel="noreferrer" aria-label={label} title={label}
                    className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-white/5 border border-white/15 text-white/85 hover:bg-[#C8A24B] hover:text-[#0B1D3A] hover:scale-105 transition-all"
                    data-testid={`footer-social-${label.toLowerCase()}`}>
                    <Icon size={22} />
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Bottom bar */}
        <div className="mt-10 pt-6 border-t border-white/10 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-sm font-nunito text-white/75">
            {termsOn && <a href={termsUrl} className="hover:text-white transition-colors" data-testid="footer-terms-link">{tr("Termos e Condições", "Terms & Conditions")}</a>}
            {privacyOn && <a href={privacyUrl} className="hover:text-white transition-colors" data-testid="footer-privacy-link">{tr("Política de Privacidade", "Privacy Policy")}</a>}
            {reclamacoesOn && (
              <a href={reclamacoes} target="_blank" rel="noreferrer" className="hover:text-white transition-colors" data-testid="footer-reclamacoes-link">{tr("Livro de Reclamações", "Complaints Book")}</a>
            )}
          </div>
          {copyright && <p className="text-sm font-nunito text-white/55">{copyright}</p>}
        </div>
      </div>
    </footer>
  );
}
